import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Card,
  CardContent,
  CardActions,
  Grid,
  Chip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  Divider,
  CircularProgress,
  Tooltip,
  Paper,
} from '@mui/material';
import {
  Close,
  Visibility,
  Send,
  ContentCopy,
  Edit,
  Delete,
  Email,
  Lock,
  FolderSpecial,
  Folder,
  Add,
  History,
  ChevronLeft,
  ChevronRight,
} from '@mui/icons-material';

interface LiveTemplateSummary {
  slug: string;
  name: string;
  description: string;
  category: string;
  titleColor: string;
}

interface LiveTemplateDetail extends LiveTemplateSummary {
  title: string;
  greeting: string;
  body: string;
  buttonText?: string;
  buttonUrl?: string;
  footer: string;
  preheader?: string;
}

interface CustomTemplate {
  _id: string;
  name: string;
  description: string;
  title: string;
  greeting: string;
  body: string;
  buttonText: string;
  buttonUrl: string;
  footer: string;
  preheader: string;
  titleColor: string;
  clonedFrom: string | null;
  createdAt: string;
  updatedAt: string;
}

interface SentEmailLogEntry {
  _id: string;
  templateId: string;
  templateName: string;
  subject: string;
  recipients: string[];
  sentByEmail: string;
  createdAt: string;
}

interface EmailTemplateManagerProps {
  onClose: () => void;
}

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'https://pawtel-app.onrender.com';

const titleColorChip = (color: string) => {
  const map: Record<string, { label: string; color: 'default' | 'success' | 'error' }> = {
    default: { label: 'Default', color: 'default' },
    green: { label: 'Green', color: 'success' },
    red: { label: 'Red', color: 'error' },
  };
  const c = map[color] || map.default;
  return <Chip label={c.label} size="small" color={c.color} sx={{ height: 22, fontSize: '0.7rem' }} />;
};

const EmailTemplateManager: React.FC<EmailTemplateManagerProps> = ({ onClose }) => {
  const [tab, setTab] = useState(0); // 0 = Live, 1 = My Templates, 2 = History
  const [liveTemplates, setLiveTemplates] = useState<LiveTemplateSummary[]>([]);
  const [customTemplates, setCustomTemplates] = useState<CustomTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });

  // Preview dialog
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewTitle, setPreviewTitle] = useState('');

  // Send dialog
  const [sendOpen, setSendOpen] = useState(false);
  const [sendTarget, setSendTarget] = useState<{ type: 'live' | 'custom'; id: string; name: string } | null>(null);
  const [sendRecipients, setSendRecipients] = useState('');
  const [sendSubject, setSendSubject] = useState('');
  const [sending, setSending] = useState(false);

  // Editor dialog (for creating/editing custom templates)
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorMode, setEditorMode] = useState<'create' | 'edit'>('create');
  const [editorId, setEditorId] = useState<string | null>(null);
  const [editorData, setEditorData] = useState({
    name: '',
    description: '',
    title: '',
    greeting: '',
    body: '',
    buttonText: '',
    buttonUrl: '',
    footer: 'The Pawtel Team',
    preheader: '',
    titleColor: 'default',
    clonedFrom: null as string | null,
  });
  const [editorSaving, setEditorSaving] = useState(false);

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState<CustomTemplate | null>(null);

  // History state
  const [historyLogs, setHistoryLogs] = useState<SentEmailLogEntry[]>([]);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyPages, setHistoryPages] = useState(0);
  const [historyLoading, setHistoryLoading] = useState(false);

  const token = localStorage.getItem('admin_token');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const [liveRes, customRes] = await Promise.all([
        fetch(`${API_BASE}/api/admin/email-templates/live`, { headers }),
        fetch(`${API_BASE}/api/admin/email-templates/custom`, { headers }),
      ]);
      if (liveRes.ok) setLiveTemplates(await liveRes.json());
      if (customRes.ok) setCustomTemplates(await customRes.json());
    } catch (err) {
      console.error('Failed to fetch templates:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchHistory = useCallback(async (page = 1) => {
    setHistoryLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/email-templates/history?page=${page}&limit=25`, { headers });
      if (res.ok) {
        const data = await res.json();
        setHistoryLogs(data.logs);
        setHistoryPage(data.pagination.page);
        setHistoryTotal(data.pagination.total);
        setHistoryPages(data.pagination.pages);
      }
    } catch (err) {
      console.error('Failed to fetch history:', err);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);
  useEffect(() => { if (tab === 2) fetchHistory(1); }, [tab, fetchHistory]);

  // Group live templates by category
  const liveByCategory = liveTemplates.reduce<Record<string, LiveTemplateSummary[]>>((acc, t) => {
    if (!acc[t.category]) acc[t.category] = [];
    acc[t.category].push(t);
    return acc;
  }, {});

  // Preview
  const handlePreviewLive = async (slug: string, name: string) => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/email-templates/live/${slug}/preview`, { headers });
      if (res.ok) {
        setPreviewHtml(await res.text());
        setPreviewTitle(name);
        setPreviewOpen(true);
      }
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to load preview', severity: 'error' });
    }
  };

  const handlePreviewCustom = async (id: string, name: string) => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/email-templates/custom/${id}/preview`, { headers });
      if (res.ok) {
        setPreviewHtml(await res.text());
        setPreviewTitle(name);
        setPreviewOpen(true);
      }
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to load preview', severity: 'error' });
    }
  };

  // Send
  const handleOpenSend = (type: 'live' | 'custom', id: string, name: string) => {
    setSendTarget({ type, id, name });
    setSendRecipients('');
    setSendSubject('');
    setSendOpen(true);
  };

  const handleSend = async () => {
    if (!sendTarget || !sendRecipients.trim()) return;
    const recipients = sendRecipients.split(',').map(e => e.trim()).filter(Boolean);
    if (recipients.length === 0) return;

    setSending(true);
    try {
      const url = sendTarget.type === 'live'
        ? `${API_BASE}/api/admin/email-templates/live/${sendTarget.id}/send`
        : `${API_BASE}/api/admin/email-templates/custom/${sendTarget.id}/send`;
      const res = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify({ recipients, subject: sendSubject || undefined }),
      });
      if (res.ok) {
        setSnackbar({ open: true, message: `Email sent to ${recipients.join(', ')}`, severity: 'success' });
        setSendOpen(false);
      } else {
        const data = await res.json();
        setSnackbar({ open: true, message: data.error || 'Failed to send', severity: 'error' });
      }
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to send email', severity: 'error' });
    } finally {
      setSending(false);
    }
  };

  // Clone live template
  const handleCloneLive = async (slug: string) => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/email-templates/live/${slug}`, { headers });
      if (!res.ok) return;
      const t: LiveTemplateDetail = await res.json();
      setEditorMode('create');
      setEditorId(null);
      setEditorData({
        name: `${t.name} (Copy)`,
        description: t.description,
        title: t.title,
        greeting: t.greeting,
        body: t.body,
        buttonText: t.buttonText || '',
        buttonUrl: t.buttonUrl || '',
        footer: t.footer,
        preheader: t.preheader || '',
        titleColor: t.titleColor,
        clonedFrom: slug,
      });
      setEditorOpen(true);
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to load template for cloning', severity: 'error' });
    }
  };

  // Edit custom template
  const handleEditCustom = async (id: string) => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/email-templates/custom/${id}`, { headers });
      if (!res.ok) return;
      const t: CustomTemplate = await res.json();
      setEditorMode('edit');
      setEditorId(id);
      setEditorData({
        name: t.name,
        description: t.description,
        title: t.title,
        greeting: t.greeting,
        body: t.body,
        buttonText: t.buttonText,
        buttonUrl: t.buttonUrl,
        footer: t.footer,
        preheader: t.preheader,
        titleColor: t.titleColor,
        clonedFrom: t.clonedFrom,
      });
      setEditorOpen(true);
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to load template', severity: 'error' });
    }
  };

  // Save editor
  const handleEditorSave = async () => {
    if (!editorData.name || !editorData.title || !editorData.greeting || !editorData.body) {
      setSnackbar({ open: true, message: 'Name, title, greeting, and body are required', severity: 'error' });
      return;
    }
    setEditorSaving(true);
    try {
      const url = editorMode === 'create'
        ? `${API_BASE}/api/admin/email-templates/custom`
        : `${API_BASE}/api/admin/email-templates/custom/${editorId}`;
      const res = await fetch(url, {
        method: editorMode === 'create' ? 'POST' : 'PUT',
        headers,
        body: JSON.stringify(editorData),
      });
      if (res.ok) {
        setSnackbar({ open: true, message: editorMode === 'create' ? 'Template created' : 'Template updated', severity: 'success' });
        setEditorOpen(false);
        fetchTemplates();
      } else {
        const data = await res.json();
        setSnackbar({ open: true, message: data.error || 'Failed to save', severity: 'error' });
      }
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to save template', severity: 'error' });
    } finally {
      setEditorSaving(false);
    }
  };

  // Delete custom template
  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      const res = await fetch(`${API_BASE}/api/admin/email-templates/custom/${deleteTarget._id}`, {
        method: 'DELETE',
        headers,
      });
      if (res.ok) {
        setSnackbar({ open: true, message: 'Template deleted', severity: 'success' });
        setDeleteTarget(null);
        fetchTemplates();
      }
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to delete template', severity: 'error' });
    }
  };

  // New blank custom template
  const handleNewCustom = () => {
    setEditorMode('create');
    setEditorId(null);
    setEditorData({
      name: '',
      description: '',
      title: '',
      greeting: 'Hello,',
      body: '',
      buttonText: '',
      buttonUrl: '',
      footer: 'The Pawtel Team',
      preheader: '',
      titleColor: 'default',
      clonedFrom: null,
    });
    setEditorOpen(true);
  };

  return (
    <Box sx={{ p: 3, minWidth: 900 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h5" fontWeight="bold">
            <Email sx={{ mr: 1, verticalAlign: 'middle' }} />
            Email Templates
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Preview, send, and manage email templates
          </Typography>
        </Box>
        <Button onClick={onClose} variant="outlined" size="small" startIcon={<Close />}>
          Close
        </Button>
      </Box>

      {/* Tabs */}
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Tab
          icon={<FolderSpecial />}
          iconPosition="start"
          label={`Live Templates (${liveTemplates.length})`}
          sx={{ textTransform: 'none' }}
        />
        <Tab
          icon={<Folder />}
          iconPosition="start"
          label={`My Templates (${customTemplates.length})`}
          sx={{ textTransform: 'none' }}
        />
        <Tab
          icon={<History />}
          iconPosition="start"
          label="Send History"
          sx={{ textTransform: 'none' }}
        />
      </Tabs>

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : tab === 0 ? (
        /* ======================== LIVE TEMPLATES ======================== */
        <Box>
          <Alert severity="info" sx={{ mb: 3 }}>
            <strong>Live Templates</strong> are the actual emails sent by the system. They are <strong>read-only</strong> to prevent accidental changes.
            You can preview them, send test emails, or clone them to create editable copies in "My Templates".
          </Alert>

          {Object.entries(liveByCategory).map(([category, templates]) => (
            <Box key={category} mb={4}>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Lock sx={{ fontSize: 18, color: 'text.secondary' }} />
                {category}
                <Chip label={`${templates.length}`} size="small" sx={{ height: 22 }} />
              </Typography>
              <Grid container spacing={2}>
                {templates.map((t) => (
                  <Grid item xs={12} sm={6} md={4} key={t.slug}>
                    <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column', borderLeft: '4px solid', borderLeftColor: t.titleColor === 'green' ? 'success.main' : t.titleColor === 'red' ? 'error.main' : 'grey.400' }}>
                      <CardContent sx={{ flex: 1 }}>
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                          <Lock sx={{ fontSize: 14, color: 'text.disabled' }} />
                          <Typography variant="subtitle2" fontWeight="bold" noWrap>
                            {t.name}
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                          {t.description}
                        </Typography>
                        <Box mt={1}>
                          {titleColorChip(t.titleColor)}
                        </Box>
                      </CardContent>
                      <Divider />
                      <CardActions sx={{ px: 2, py: 1 }}>
                        <Tooltip title="Preview">
                          <IconButton size="small" onClick={() => handlePreviewLive(t.slug, t.name)}>
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Send Test Email">
                          <IconButton size="small" onClick={() => handleOpenSend('live', t.slug, t.name)}>
                            <Send fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Clone to My Templates">
                          <IconButton size="small" onClick={() => handleCloneLive(t.slug)}>
                            <ContentCopy fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          ))}
        </Box>
      ) : tab === 1 ? (
        /* ======================== MY TEMPLATES ======================== */
        <Box>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Alert severity="success" sx={{ flex: 1, mr: 2 }}>
              <strong>My Templates</strong> are your editable copies. Clone a live template or create one from scratch.
              You can edit, preview, send, and delete these freely.
            </Alert>
            <Button variant="contained" startIcon={<Add />} onClick={handleNewCustom} sx={{ whiteSpace: 'nowrap' }}>
              New Template
            </Button>
          </Box>

          {customTemplates.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Email sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">No custom templates yet</Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Clone a live template or create a new one to get started.
              </Typography>
              <Button variant="outlined" onClick={() => setTab(0)}>
                Browse Live Templates
              </Button>
            </Paper>
          ) : (
            <Grid container spacing={2}>
              {customTemplates.map((t) => (
                <Grid item xs={12} sm={6} md={4} key={t._id}>
                  <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column', borderLeft: '4px solid', borderLeftColor: t.titleColor === 'green' ? 'success.main' : t.titleColor === 'red' ? 'error.main' : 'grey.400' }}>
                    <CardContent sx={{ flex: 1 }}>
                      <Typography variant="subtitle2" fontWeight="bold" noWrap>
                        {t.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                        {t.description || 'No description'}
                      </Typography>
                      <Box mt={1} display="flex" gap={0.5} flexWrap="wrap">
                        {titleColorChip(t.titleColor)}
                        {t.clonedFrom && (
                          <Chip label={`Cloned from: ${t.clonedFrom}`} size="small" variant="outlined" sx={{ height: 22, fontSize: '0.65rem' }} />
                        )}
                      </Box>
                      <Typography variant="caption" color="text.disabled" display="block" mt={1}>
                        Updated {new Date(t.updatedAt).toLocaleDateString()}
                      </Typography>
                    </CardContent>
                    <Divider />
                    <CardActions sx={{ px: 2, py: 1 }}>
                      <Tooltip title="Preview">
                        <IconButton size="small" onClick={() => handlePreviewCustom(t._id, t.name)}>
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => handleEditCustom(t._id)}>
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Send Email">
                        <IconButton size="small" onClick={() => handleOpenSend('custom', t._id, t.name)}>
                          <Send fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => setDeleteTarget(t)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      ) : tab === 2 ? (
        /* ======================== SEND HISTORY ======================== */
        <Box>
          {historyLoading ? (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress />
            </Box>
          ) : historyLogs.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <History sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">No emails sent yet</Typography>
              <Typography variant="body2" color="text.secondary">
                Send a custom template to see the history here.
              </Typography>
            </Paper>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Showing {historyLogs.length} of {historyTotal} sent emails
              </Typography>
              {historyLogs.map((log) => (
                <Paper key={log._id} variant="outlined" sx={{ p: 2, mb: 1.5, display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Send sx={{ color: 'text.disabled', fontSize: 20 }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                      <Typography variant="subtitle2" fontWeight="bold" noWrap>
                        {log.templateName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        — {log.subject}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      To: {log.recipients.join(', ')}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {new Date(log.createdAt).toLocaleDateString()} {new Date(log.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </Typography>
                    {log.sentByEmail && (
                      <Typography variant="caption" color="text.disabled">
                        by {log.sentByEmail}
                      </Typography>
                    )}
                  </Box>
                </Paper>
              ))}

              {/* Pagination */}
              {historyPages > 1 && (
                <Box display="flex" justifyContent="center" alignItems="center" gap={2} mt={3}>
                  <Button
                    size="small"
                    disabled={historyPage <= 1}
                    onClick={() => fetchHistory(historyPage - 1)}
                    startIcon={<ChevronLeft />}
                  >
                    Previous
                  </Button>
                  <Typography variant="body2" color="text.secondary">
                    Page {historyPage} of {historyPages}
                  </Typography>
                  <Button
                    size="small"
                    disabled={historyPage >= historyPages}
                    onClick={() => fetchHistory(historyPage + 1)}
                    endIcon={<ChevronRight />}
                  >
                    Next
                  </Button>
                </Box>
              )}
            </>
          )}
        </Box>
      ) : null}

      {/* ======================== PREVIEW DIALOG ======================== */}
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Preview: {previewTitle}
          <IconButton onClick={() => setPreviewOpen(false)}><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box
            sx={{
              border: '1px solid #e0e0e0',
              borderRadius: 1,
              overflow: 'hidden',
              bgcolor: '#f4f4f7',
            }}
          >
            <iframe
              title="Email Preview"
              srcDoc={previewHtml}
              style={{ width: '100%', height: '600px', border: 'none' }}
            />
          </Box>
        </DialogContent>
      </Dialog>

      {/* ======================== SEND DIALOG ======================== */}
      <Dialog open={sendOpen} onClose={() => setSendOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Send: {sendTarget?.name}
        </DialogTitle>
        <DialogContent>
          <TextField
            label="Recipients (comma-separated)"
            fullWidth
            value={sendRecipients}
            onChange={(e) => setSendRecipients(e.target.value)}
            placeholder="email@example.com, another@example.com"
            sx={{ mt: 2 }}
            helperText="Maximum 10 recipients"
          />
          <TextField
            label="Subject (optional)"
            fullWidth
            value={sendSubject}
            onChange={(e) => setSendSubject(e.target.value)}
            placeholder="Leave blank for default subject"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSendOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSend}
            disabled={sending || !sendRecipients.trim()}
            startIcon={sending ? <CircularProgress size={16} /> : <Send />}
          >
            {sending ? 'Sending...' : 'Send'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ======================== EDITOR DIALOG ======================== */}
      <Dialog open={editorOpen} onClose={() => setEditorOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editorMode === 'create' ? 'Create Template' : 'Edit Template'}
          {editorData.clonedFrom && (
            <Chip label={`Cloned from: ${editorData.clonedFrom}`} size="small" variant="outlined" sx={{ ml: 2 }} />
          )}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Template Name"
                fullWidth
                required
                value={editorData.name}
                onChange={(e) => setEditorData(d => ({ ...d, name: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Title Bar Color</InputLabel>
                <Select
                  value={editorData.titleColor}
                  label="Title Bar Color"
                  onChange={(e) => setEditorData(d => ({ ...d, titleColor: e.target.value }))}
                >
                  <MenuItem value="default">Default (Grey)</MenuItem>
                  <MenuItem value="green">Green (Success)</MenuItem>
                  <MenuItem value="red">Red (Alert)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Description"
                fullWidth
                value={editorData.description}
                onChange={(e) => setEditorData(d => ({ ...d, description: e.target.value }))}
                placeholder="Brief description of this template"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Email Title"
                fullWidth
                required
                value={editorData.title}
                onChange={(e) => setEditorData(d => ({ ...d, title: e.target.value }))}
                placeholder="e.g. Welcome to Pawtel!"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Preheader Text"
                fullWidth
                value={editorData.preheader}
                onChange={(e) => setEditorData(d => ({ ...d, preheader: e.target.value }))}
                placeholder="Preview text shown in email client inbox"
                helperText="Shows as preview text in email clients before opening"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Greeting"
                fullWidth
                required
                value={editorData.greeting}
                onChange={(e) => setEditorData(d => ({ ...d, greeting: e.target.value }))}
                placeholder="e.g. Hello John,"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Body (HTML)"
                fullWidth
                required
                multiline
                rows={8}
                value={editorData.body}
                onChange={(e) => setEditorData(d => ({ ...d, body: e.target.value }))}
                placeholder="<p>Your email content here...</p>"
                helperText="Supports HTML: <p>, <ul>, <li>, <strong>, <em>, <h1>-<h6>, etc."
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Button Text (optional)"
                fullWidth
                value={editorData.buttonText}
                onChange={(e) => setEditorData(d => ({ ...d, buttonText: e.target.value }))}
                placeholder="e.g. Get Started"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Button URL (optional)"
                fullWidth
                value={editorData.buttonUrl}
                onChange={(e) => setEditorData(d => ({ ...d, buttonUrl: e.target.value }))}
                placeholder="https://pawtel.co"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Footer Sign-off"
                fullWidth
                value={editorData.footer}
                onChange={(e) => setEditorData(d => ({ ...d, footer: e.target.value }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditorOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleEditorSave}
            disabled={editorSaving}
            startIcon={editorSaving ? <CircularProgress size={16} /> : undefined}
          >
            {editorSaving ? 'Saving...' : editorMode === 'create' ? 'Create Template' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ======================== DELETE CONFIRMATION ======================== */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs">
        <DialogTitle>Delete Template</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{deleteTarget?.name}</strong>? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* ======================== SNACKBAR ======================== */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EmailTemplateManager;
